import { IInventoryPurchaseOrderCreateFromSheetResponse } from "@/api_framework/api_modals/Inventory";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCreatePOFromSheetAPI } from "@/app/hooks/api_hooks/Inventory/PO/useCreatePOFromSheetAPI";
import { usePOSheetTabsAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePOSheetTabsAPI";
import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useLoader } from "@/app/hooks/useLoader";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

interface ICreatePOFromSheetFormFields {
  tab: string;
}

interface ICreatePOFromSheetModalProps {
  onCreateSuccess?: (response: IInventoryPurchaseOrderCreateFromSheetResponse) => void;
}

const CreatePOFromSheetModal: React.FC<ICreatePOFromSheetModalProps> = ({ onCreateSuccess }) => {
  const isModalOpen = useModal("CREATE_PO_FROM_SHEET_MODAL");
  const { setInvisible } = useModalActions("CREATE_PO_FROM_SHEET_MODAL");
  const createFromSheet = useCreatePOFromSheetAPI();
  const { execute: fetchSheetTabs, sheetTabs, isLoading: isFetchingTabs } = usePOSheetTabsAPI();
  const isInProgress = useLoader("isCreatePOFromSheetInProgress");

  const formHook = useForm<ICreatePOFromSheetFormFields>({
    mode: "onChange",
    defaultValues: {
      tab: "",
    },
  });

  const selectedTab = formHook.watch("tab");
  const tabOptions = useSelectMenuReducer(sheetTabs, "tab", "tab");

  useEffect(() => {
    if (isModalOpen) {
      formHook.reset({ tab: "" });
      fetchSheetTabs();
    }
  }, [isModalOpen]);

  const handleConfirm = async () => {
    if (!selectedTab) return;
    try {
      const response = await createFromSheet({ tab: selectedTab });
      if (response?.status === true) {
        onCreateSuccess?.(response);
        formHook.reset({ tab: "" });
        setInvisible();
      }
    } catch (error) {
      toast.error("Failed to create purchase orders. Please try again.");
      console.error("Create PO from sheet error:", error);
    }
  };

  const handleCancel = () => {
    formHook.reset({ tab: "" });
    setInvisible();
  };

  return (
    <ModalPrompt
      modalId="CREATE_PO_FROM_SHEET_MODAL"
      showIcon={false}
      isshowCrossIcon={true}
      titleIcon=""
      dialogClassName="sm:my-8 sm:w-full sm:max-w-md sm:p-6 w-full"
      contentOverflow={true}
      button={{
        primaryButtonText: isInProgress ? "Creating..." : "Yes, Create",
        secondaryButtonText: "Cancel",
        primaryButtonDisabled: !selectedTab || isInProgress || isFetchingTabs,
        secondaryButtonDisabled: isInProgress,
        primaryButtonClassname:
          "px-6 cursor-pointer py-[9px] bg-sky-900 hover:bg-sky-800 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex",
        primaryCallback: handleConfirm,
        secondaryCallback: handleCancel,
        shouldHideOnClick: false,
      }}
      title="Bulk Create Purchase Orders"
      content={
        <div className="text-sm text-gray-600 mt-2">
          {isInProgress ? (
            <div className="flex items-center gap-2 py-2">
              <CircleSpinnerLoader h="20" w="20" />
              <span>Creating purchase orders from the sheet...</span>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-700 font-semibold">
                Select a sheet tab <span className="text-red-600"> *</span>
              </p>
              <div className="w-full">
                {isFetchingTabs ? (
                  <div className="flex items-center gap-2 py-2 text-gray-500">
                    <CircleSpinnerLoader h="16" w="16" />
                    <span>Loading sheet tabs...</span>
                  </div>
                ) : (
                  <SearchableSelectMenu
                    errorMessages={[{ type: "required", message: "Sheet tab is required" }]}
                    fieldError={formHook.formState.errors.tab}
                    register={formHook.register("tab", { required: true })}
                    showDropdownIcon
                    placeholder="Select Sheet Tab"
                    selectItems={tabOptions}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("tab", item.id);
                        formHook.clearErrors("tab");
                      } else {
                        formHook.setValue("tab", "");
                      }
                    }}
                    defaultSelected={tabOptions?.find((opt) => opt.id === selectedTab)}
                    listBoxClassName="w-full"
                    containerClassName="w-full"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};

export default CreatePOFromSheetModal;
